import { Howl, Howler } from "howler";

// Define the available sound keys
export type SoundKey = "background" | "click" | "swoosh";

// Configuration interface for sound initialization
interface SoundConfig {
  src: string[];
  autoplay?: boolean;
  loop?: boolean;
  volume?: number;
}

// Sound registry type
type SoundRegistry = {
  [K in SoundKey]: SoundConfig;
};

// Define all available sounds and their configurations
const SOUND_REGISTRY: SoundRegistry = {
  background: {
    src: ["/audio/sfx/background.mp3"],
    autoplay: false,
    loop: true,
    volume: 0.04,
  },
  click: {
    src: ["/audio/sfx/click.mp3"],
    loop: false,
    volume: 0.1,
  },
  swoosh: {
    src: ["/audio/sfx/swoosh.mp3"],
    loop: false,
    volume: 0.3,
  },
};

export class AudioManager {
  private static instance: AudioManager; // Static instance
  private sounds: Map<SoundKey, Howl>;
  private loadingPromises: Map<SoundKey, Promise<Howl>>;
  private isInitialized: boolean;
  private soundEnabled: boolean;

  // Private constructor to prevent direct instantiation
  private constructor() {
    this.sounds = new Map();
    this.loadingPromises = new Map();
    this.isInitialized = false;
    this.soundEnabled = true;
  }

  // Public method to get the singleton instance
  public static getInstance(): AudioManager {
    if (!AudioManager.instance) {
      AudioManager.instance = new AudioManager();
    }
    return AudioManager.instance;
  }

  private registerSound(key: SoundKey, config: SoundConfig): Promise<Howl> {
    // Create a promise that resolves when the sound is loaded
    const loadingPromise = new Promise<Howl>((resolve, reject) => {
      const sound = new Howl({
        ...config,
        onload: () => resolve(sound),
        onloaderror: (_id: number, error: unknown) =>
          reject(new Error(`Failed to load sound ${key}: ${error}`)),
      });
      this.sounds.set(key, sound);
    });

    this.loadingPromises.set(key, loadingPromise);
    return loadingPromise;
  }

  public async initialize(): Promise<void> {
    if (this.isInitialized) return;

    try {
      // Register all sounds from the registry
      const loadingPromises = Object.entries(SOUND_REGISTRY).map(
        ([key, config]) => this.registerSound(key as SoundKey, config),
      );

      // Wait for all sounds to load
      await Promise.all(loadingPromises);
      this.isInitialized = true;
      console.log("All audio assets loaded successfully");
    } catch (error) {
      console.error("Failed to initialize audio system:", error);
      throw error;
    }
  }

  public play(key: SoundKey): number | undefined {
    if (!this.isInitialized) {
      console.warn(
        "Attempted to play sound before audio system was initialized",
      );
      return;
    }

    const sound = this.sounds.get(key);
    if (sound && this.soundEnabled) {
      if (sound.playing()) {
        return;
      }
      return sound.play();
    } else {
      console.warn(`Sound with key "${key}" not found`);
      return undefined;
    }

  }

  public stop(key: SoundKey): void {
    const sound = this.sounds.get(key);
    if (sound) {
      sound.stop();
    }
  }

  public setVolume(key: SoundKey, volume: number): void {
    if (volume < 0 || volume > 1) {
      console.warn("Volume must be between 0 and 1");
      return;
    }

    const sound = this.sounds.get(key);
    if (sound) {
      sound.volume(volume);
    }
  }

  public setMasterVolume(volume: number): void {
    if (volume < 0 || volume > 1) {
      console.warn("Volume must be between 0 and 1");
      return;
    }
    Howler.volume(volume);
  }

  public setEnabled(enabled: boolean): void {
    this.soundEnabled = enabled;
    if (!enabled) {
      this.sounds.forEach((sound) => sound.stop());
    }
  }

  public isPlaying(key: SoundKey): boolean {
    const sound = this.sounds.get(key);
    return sound ? sound.playing() : false;
  }

  public fade(key: SoundKey, from: number, to: number, duration: number): void {
    const sound = this.sounds.get(key);
    if (sound) {
      sound.fade(from, to, duration);
    }
  }

  // Utility method to check if the system is initialized
  public get initialized(): boolean {
    return this.isInitialized;
  }

  public get enabled(): boolean {
    return this.soundEnabled;
  }
}

const audioManager = AudioManager.getInstance();
export default audioManager;
